import {
  AbsenceAttachment,
  AbsenceStatus,
  LongAbsence,
  ShortAbsence,
  submitLongAbsence,
  submitShortAbsence,
} from '~/data/absences';
import { AbsenceEntry, AbsenceType } from '~/data/absences/types';
import { Enum } from '~/data/enums';
import {
  AbsenceReasonFieldValue,
  YearFieldValue,
} from '~/features/AbsenceChanges/EditAbsenceDialog/useAbsenceForm';
import { getFormattedDate } from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import { capitalizeFirstLetter } from '~/utils/helper';

type ShortAbsenceSubmission = Omit<
  ShortAbsence,
  'fAbsenceShortStartDate' | 'fAbsenceShortStatus'
>;

type LongAbsenceSubmission = Omit<
  LongAbsence,
  'fAbsenceLongStartDate' | 'fAbsenceLongStatus'
>;

export const getAbsenceSubmissionJson = (
  absenceType: AbsenceType,
  data: AbsenceForm,
): ShortAbsenceSubmission | LongAbsenceSubmission => {
  /**
   * NOTE: Prop names in common json will be prefixed with absence type
   * specific string for the final json output
   * Example: startDate => fAbsenceShortStartDate
   */
  const commonNonPrefixedJsonProps = {
    attachmentId: data.attachmentId ?? -1,
    attachmentName: data.attachmentName ?? '',
    endDate: !!data.endDate ? getFormattedDate(data.endDate, 'YYYY-MM-DD') : '',
    info: data.info,
    reason: data.absenceReason?.code,
    startDate: !!data.startDate
      ? getFormattedDate(data.startDate, 'YYYY-MM-DD')
      : '',
    status: AbsenceStatus.PROPOSED,
  };

  const attachmentFields = ['attachmentId', 'attachmentName'];

  const prefixJsonProps = (data: object, prefix: string) => {
    return Object.fromEntries(
      Object.entries(data)
        .filter(([key]) => !attachmentFields.includes(key))
        .map(([key, value]) => [
          `${prefix}${capitalizeFirstLetter(key)}`,
          value,
        ]),
    );
  };

  const prefixAttachmentJsonProps = <
    TAttachment extends {
      attachmentId: number;
      attachmentName: string;
    },
  >(
    data: TAttachment,
    propName: string,
  ) => {
    const attachmentObject: AbsenceAttachment = {
      fAttachmentId: data.attachmentId,
      fAttachmentName: data.attachmentName,
    };

    return Object.fromEntries([[propName, attachmentObject]]);
  };

  const getShortAbsence = (): ShortAbsenceSubmission => {
    const yearCode =
      typeof data.year?.code !== 'undefined'
        ? parseInt(data.year?.code)
        : undefined;

    const jsonProps: Omit<ShortAbsenceSubmission, 'fAbsenceShortAttachment'> = {
      ...prefixJsonProps(commonNonPrefixedJsonProps, 'fAbsenceShort'),
      fAbsenceShortDays: data.days,
      fAbsenceShortAbsenceHours: data.hours ?? 0,
      fAbsenceShortPlannedWorkingHours: data.plannedWorkingHours ?? 0,
      ...(!!yearCode && !isNaN(yearCode)
        ? { fAbsenceShortBalanceYear: yearCode }
        : {}),
    };

    const jsonAttachment: Pick<
      ShortAbsenceSubmission,
      'fAbsenceShortAttachment'
    > = prefixAttachmentJsonProps(
      commonNonPrefixedJsonProps,
      'fAbsenceShortAttachment',
    );

    return {
      ...jsonProps,
      ...jsonAttachment,
    };
  };

  const getLongAbsence = (): LongAbsenceSubmission => {
    const jsonProps: Omit<LongAbsenceSubmission, 'fAbsenceLongAttachment'> =
      prefixJsonProps(commonNonPrefixedJsonProps, 'fAbsenceLong');

    const jsonAttachment: Pick<
      LongAbsenceSubmission,
      'fAbsenceLongAttachment'
    > = prefixAttachmentJsonProps(
      commonNonPrefixedJsonProps,
      'fAbsenceLongAttachment',
    );

    return { ...jsonProps, ...jsonAttachment };
  };

  return absenceType === AbsenceType.SHORT
    ? getShortAbsence()
    : getLongAbsence();
};

export const getAbsenceSubmissionFormData = (
  submissionJson: ShortAbsenceSubmission | LongAbsenceSubmission,
  attachment: File,
): FormData => {
  const fd = new FormData();
  Object.keys(submissionJson).forEach((key, index) => {
    if (key !== 'file') {
      fd.append(key, Object.values(submissionJson)[index] as string);
    }
  });
  fd.append('file', attachment as Blob, attachment.name);

  return fd;
};

const getSubmissionData = <TAbsence extends ShortAbsence | LongAbsence>(
  absenceType: AbsenceType,
  data: AbsenceForm,
): TAbsence | FormData => {
  const submissionJson = getAbsenceSubmissionJson(absenceType, data);
  const attachment = data.attachment;

  return !!attachment
    ? getAbsenceSubmissionFormData(submissionJson, attachment)
    : (submissionJson as TAbsence);
};

export const submitFormAbsence = (props: {
  employeeId: number;
  absenceType: AbsenceType;
  absenceId?: string;
  data: AbsenceForm;
}): Promise<ShortAbsence | LongAbsence> => {
  const { employeeId, absenceType, data, absenceId } = props;

  if (absenceType === AbsenceType.LONG) {
    return submitLongAbsence({
      employeeId,
      absenceId,
      data: getSubmissionData<LongAbsence>(absenceType, data),
    });
  } else {
    return submitShortAbsence({
      employeeId,
      absenceId,
      data: getSubmissionData<ShortAbsence>(absenceType, data),
    });
  }
};

export type AbsenceForm = {
  absenceReason?: AbsenceReasonFieldValue;
  year?: YearFieldValue;
  startDate: Date | null;
  endDate: Date | null;
  days?: number;
  hours?: number;
  plannedWorkingHours?: number;
  info?: string;

  attachmentId: number;
  attachmentName: string;
  attachment?: File | null;
};

export const resolveAbsenceFormData = (
  absenceEntry?: AbsenceEntry,
): AbsenceForm => {
  const defaultFormData: AbsenceForm = {
    absenceReason: null,
    startDate: null,
    endDate: null,
    days: 0,
    hours: 0,
    plannedWorkingHours: 0,
    info: '',

    attachmentId: -1,
    attachmentName: '',
    attachment: undefined,
  };

  const resolveCommonFormData = (entry: AbsenceEntry) => {
    const { reason, startDate, endDate, info, attachment } = entry;

    return {
      absenceReason: reason ?? null,
      startDate: startDate ?? null,
      endDate: endDate ?? null,
      info: info ?? '',

      attachmentId: attachment?.id ?? -1,
      attachmentName: attachment?.name ?? '',
      attachment: undefined,
    };
  };

  if (absenceEntry?.type === AbsenceType.SHORT) {
    const { days, year, hours, plannedWorkingHours } = absenceEntry;

    const getYearEnum = (year?: number): Enum | null => {
      if (typeof year === 'number') {
        return {
          code: year.toString(),
          name: year.toString(),
        };
      }
      return null;
    };

    return {
      ...resolveCommonFormData(absenceEntry),
      year: getYearEnum(year),
      days: days ?? 0,
      hours: hours ?? 0,
      plannedWorkingHours: plannedWorkingHours ?? 0,
    };
  } else if (absenceEntry?.type === AbsenceType.LONG) {
    return {
      ...resolveCommonFormData(absenceEntry),
      days: 0,
    };
  }

  return defaultFormData;
};
